// Phase 4 — Agents IA spécialisés
import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { TrendingUp, ShieldAlert, Scale, Sun, Send, Bot, User, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useEntity } from "@/lib/entity";
import { useAuth } from "@/lib/auth";

const AGENTS = [
  {
    id: "financial",
    name: "Analyste Financier",
    icon: TrendingUp,
    color: "#D6F32F",
    description: "Analyse CA, charges, trésorerie, anomalies. Identifie les signaux faibles et propose des actions concrètes.",
    starters: [
      "Analyse la santé financière de l'entité courante",
      "Quels sont les 3 principaux risques financiers du mois ?",
      "Compare les performances entre entités",
      "Propose un plan d'amélioration de la marge",
    ],
  },
  {
    id: "hse",
    name: "Veilleur HSE",
    icon: ShieldAlert,
    color: "#F59E0B",
    description: "Surveille les incidents HSE, détecte des patterns, propose plans de prévention selon la réglementation gabonaise.",
    starters: [
      "Analyse les tendances des incidents récents",
      "Identifie les zones à risque prioritaires",
      "Propose un programme de prévention 30 jours",
      "Rappelle les obligations légales HSE au Gabon",
    ],
  },
  {
    id: "legal",
    name: "Conseiller Juridique",
    icon: Scale,
    color: "#8B5CF6",
    description: "Expert OHADA et droit gabonais. Rédige courriers, contrats, signale échéances critiques.",
    starters: [
      "Rédige un courrier de relance pour facture impayée",
      "Liste les contrats arrivant à échéance",
      "Quelles sont les obligations TVA mensuelles au Gabon ?",
      "Rédige une mise en demeure pour défaut de paiement",
    ],
  },
  {
    id: "briefing",
    name: "Briefing Exécutif",
    icon: Sun,
    color: "#22C55E",
    description: "Synthèse matinale du PDG : chiffres clés, alertes, échéances, focus stratégique du jour.",
    starters: [
      "Prépare mon briefing matinal",
      "Quelles sont les priorités de la journée ?",
      "Synthétise la semaine écoulée",
      "Que dois-je communiquer à mon comité ?",
    ],
  },
];

export default function AIAgents() {
  const [selected, setSelected] = useState(null);
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="ai-agents-page">
      <div>
        <div className="overline text-citron mb-2">Intelligence Augmentée</div>
        <h2 className="font-heading text-3xl font-bold tracking-tight">Agents IA Spécialisés</h2>
        <p className="text-sm text-obsidian-400 mt-1">Copilotes experts propulsés par Claude Sonnet 4.5, contextualisés sur vos données.</p>
      </div>

      {!selected ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {AGENTS.map((a) => (
            <button
              key={a.id}
              onClick={() => setSelected(a)}
              className="card p-6 text-left hover:bg-obsidian-850 hover:border-citron/40 transition-all group"
              data-testid={`ai-agent-${a.id}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 grid place-items-center rounded-sm" style={{ background: a.color + "20" }}>
                  <a.icon size={18} style={{ color: a.color }} />
                </div>
                <div>
                  <div className="font-heading text-lg font-bold tracking-tight">{a.name}</div>
                  <div className="overline text-[9px]">Claude Sonnet 4.5</div>
                </div>
              </div>
              <p className="text-sm text-obsidian-300 leading-relaxed">{a.description}</p>
              <div className="mt-4 text-xs text-citron group-hover:underline">Activer l'agent →</div>
            </button>
          ))}
        </div>
      ) : (
        <AgentChat agent={selected} onClose={() => setSelected(null)} />
      )}
    </motion.div>
  );
}

function AgentChat({ agent, onClose }) {
  const { currentEntityId, currentEntity } = useEntity();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = `agent_${agent.id}_${user?.id}`;

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  async function send(text) {
    const m = (text ?? input).trim();
    if (!m || loading) return;
    setInput("");
    setMessages((arr) => [...arr, { role: "user", content: m }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/agent", {
        agent: agent.id,
        message: m,
        entity_id: currentEntityId,
        session_id: sessionId,
      });
      setMessages((arr) => [...arr, { role: "assistant", content: data.response }]);
    } catch (e) {
      setMessages((arr) => [...arr, { role: "assistant", content: `⚠️ ${e?.response?.data?.detail || e.message}`, error: true }]);
    } finally { setLoading(false); }
  }

  return (
    <div className="card flex flex-col h-[70vh]" data-testid={`ai-agent-chat-${agent.id}`}>
      <div className="px-5 py-3 border-b border-line flex items-center gap-3">
        <button onClick={onClose} className="btn-ghost h-8 px-2 text-xs">← Retour</button>
        <div className="w-8 h-8 grid place-items-center rounded-sm" style={{ background: agent.color + "20" }}>
          <agent.icon size={14} style={{ color: agent.color }} />
        </div>
        <div>
          <div className="font-heading font-bold text-sm">{agent.name}</div>
          <div className="text-[10px] text-obsidian-400 font-mono">Contexte: {currentEntity?.name}</div>
        </div>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
        {messages.length === 0 && (
          <div className="space-y-3">
            <div className="overline" style={{ color: agent.color }}>Démarrez avec une question</div>
            {agent.starters.map((s, i) => (
              <button
                key={i}
                onClick={() => send(s)}
                className="w-full text-left p-3 bg-obsidian-850 hover:bg-obsidian-800 border border-line hover:border-citron/40 rounded-sm text-sm transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
            <div className={`w-7 h-7 flex-shrink-0 grid place-items-center rounded-sm ${m.role === "user" ? "bg-obsidian-700" : ""}`} style={m.role !== "user" ? { background: agent.color + "30", color: agent.color } : {}}>
              {m.role === "user" ? <User size={13} /> : <Bot size={13} />}
            </div>
            <div className={`max-w-[80%] px-3.5 py-2.5 rounded-sm text-sm leading-relaxed ${m.role === "user" ? "bg-obsidian-800" : m.error ? "bg-bad/10 border border-bad/30 text-bad" : "bg-obsidian-850 border border-line text-obsidian-100"}`}>
              <div className="whitespace-pre-wrap break-words">{m.content}</div>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex items-center gap-2 text-xs text-obsidian-400">
            <Loader2 size={14} className="animate-spin" style={{ color: agent.color }} />
            <span>{agent.name} analyse…</span>
          </div>
        )}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-4 border-t border-line">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={`Posez une question à ${agent.name}…`}
            rows={1}
            className="input min-h-[40px] max-h-32 resize-none py-2.5"
            data-testid={`agent-${agent.id}-input`}
          />
          <button type="submit" disabled={loading || !input.trim()} className="btn-primary h-10 w-10 p-0" data-testid={`agent-${agent.id}-send`}>
            <Send size={14} />
          </button>
        </div>
      </form>
    </div>
  );
}
